import React, { useState } from "react";
import { useNavigate, Link } from "react-router-dom";
import { Loader2, ShieldCheck, ArrowLeft, CheckCircle2 } from "lucide-react";
import { useAuth } from "@/context/AuthContext";
import { apiError } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";

const PERKS = [
  "Receive priced remedial leads in your postcode areas",
  "Quote directly to building owners — no cold calling",
  "Only pay a fee when you win the work",
];

export default function ContractorApply() {
  const { registerContractor } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ company_name: "", contact_name: "", email: "", password: "", postcode: "" });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const set = (k) => (e) => setF((s) => ({ ...s, [k]: e.target.value }));

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!f.company_name.trim() || !f.email.trim() || f.password.length < 8) {
      setError("Company name, email and a password (min 8 characters) are required.");
      return;
    }
    setLoading(true);
    try {
      await registerContractor({ ...f, email: f.email.trim().toLowerCase() });
      toast.success("Welcome — let's build your profile");
      navigate("/partner", { replace: true });
    } catch (err) {
      setError(apiError(err, "Could not create your account."));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen bg-slate-950 px-4 py-10 text-white">
      <div className="mx-auto grid w-full max-w-5xl items-center gap-10 lg:grid-cols-2">
        <div className="hidden lg:block">
          <Link to="/" className="mb-8 inline-flex items-center gap-2 text-sm text-slate-400 hover:text-white" data-testid="partner-apply-back">
            <ArrowLeft className="h-4 w-4" /> Back to FD Check
          </Link>
          <span className="inline-flex items-center gap-2 rounded-full border border-red-400/30 bg-red-400/10 px-3 py-1 text-xs font-semibold uppercase tracking-wider text-red-300">
            <ShieldCheck className="h-3.5 w-3.5" /> Fire door contractor network
          </span>
          <h1 className="mt-5 font-['Sora',sans-serif] text-4xl font-extrabold leading-tight">
            Win vetted fire-door remedial work in your area
          </h1>
          <p className="mt-4 text-slate-300">
            Join the FD Check contractor network. When an inspection flags remedial works in
            your postcode areas, you get the opportunity to quote.
          </p>
          <ul className="mt-6 space-y-3">
            {PERKS.map((p) => (
              <li key={p} className="flex items-start gap-3 text-slate-200">
                <CheckCircle2 className="mt-0.5 h-5 w-5 shrink-0 text-red-400" /> {p}
              </li>
            ))}
          </ul>
        </div>

        <div className="rounded-2xl bg-white p-6 text-slate-900 shadow-2xl sm:p-8">
          <h2 className="font-['Sora',sans-serif] text-2xl font-bold">Apply to join</h2>
          <p className="mt-1 text-sm text-slate-500">Free to apply — you&apos;ll build your profile next.</p>
          <form onSubmit={submit} className="mt-6 space-y-4">
            <Field label="Company name *"><Input value={f.company_name} onChange={set("company_name")} data-testid="partner-apply-company" /></Field>
            <Field label="Your name"><Input value={f.contact_name} onChange={set("contact_name")} data-testid="partner-apply-contact" /></Field>
            <Field label="Email *"><Input type="email" value={f.email} onChange={set("email")} data-testid="partner-apply-email" /></Field>
            <div className="grid grid-cols-2 gap-3">
              <Field label="Password * (min 8)"><Input type="password" value={f.password} onChange={set("password")} data-testid="partner-apply-password" /></Field>
              <Field label="Base postcode"><Input value={f.postcode} onChange={set("postcode")} data-testid="partner-apply-postcode" /></Field>
            </div>
            {error && <p className="rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700" data-testid="partner-apply-error">{error}</p>}
            <Button type="submit" size="lg" className="w-full bg-red-600 hover:bg-red-700" disabled={loading} data-testid="partner-apply-submit">
              {loading ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null} Create contractor account
            </Button>
          </form>
          <p className="mt-5 text-center text-sm text-slate-500">
            Already registered? <Link to="/login" className="font-medium text-red-600 hover:underline" data-testid="partner-apply-signin">Sign in</Link>
          </p>
        </div>
      </div>
    </div>
  );
}

function Field({ label, children }) {
  return <div className="space-y-1.5"><Label>{label}</Label>{children}</div>;
}
